// src/components/store/sections/product-cards/SpotlightPodiumProductCard.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { Star, ShoppingBag, Clock, Flame, Check } from "lucide-react";
import { UniversalProductCardProps, useCardActions } from "./useCardActions";

const PODIUM_THEMES = [
  "from-indigo-950 via-purple-950 to-slate-950 border-purple-700/40",
  "from-slate-950 via-cyan-950 to-slate-900 border-cyan-700/40",
  "from-rose-950 via-purple-950 to-slate-950 border-rose-700/40",
  "from-emerald-950 via-teal-950 to-slate-950 border-emerald-700/40",
];

export default function SpotlightPodiumProductCard({
  product,
  index = 0,
  subdomain = "nextlook",
}: UniversalProductCardProps) {
  const {
    productImg,
    productName,
    productUrl,
    categoryName,
    rating,
    currentPrice,
    comparePrice,
    discountPercent,
    added,
    timer,
    handleAddToCart,
  } = useCardActions(product, subdomain);

  const bgTheme = PODIUM_THEMES[index % PODIUM_THEMES.length];

  return (
    <div
      className={`relative w-full h-full rounded-[30px] p-5 overflow-hidden text-white flex flex-col justify-between shadow-2xl bg-gradient-to-br ${bgTheme} border group select-none min-h-[410px]`}
    >
      <div className="relative z-10 flex items-start justify-between gap-2">
        <div className="space-y-1 min-w-0">
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-rose-500 text-white font-black text-[10px] shadow-sm">
            <Flame className="w-3 h-3 fill-white" />
            <span>{discountPercent}% OFF</span>
          </span>
          <span className="text-[10px] font-bold text-cyan-300 uppercase tracking-wider block truncate pt-1">
            {categoryName}
          </span>
          <Link
            href={productUrl}
            className="text-base font-black text-white hover:text-cyan-300 transition-colors line-clamp-1 block"
          >
            {productName}
          </Link>
        </div>

        <div className="text-right shrink-0">
          <div className="text-lg font-black text-cyan-300 font-mono">
            ৳{currentPrice.toLocaleString()}
          </div>
          {comparePrice > currentPrice && (
            <span className="text-[10px] text-slate-400 line-through font-mono block">
              ৳{comparePrice.toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {/* 3D Glowing Podium Stage */}
      <Link href={productUrl} className="relative z-10 w-full h-44 my-2 flex items-center justify-center block">
        <div className="absolute bottom-1 w-40 h-8 rounded-full bg-purple-500/35 blur-md border border-purple-400/40" />
        <div className="relative w-36 h-36 group-hover:scale-110 transition-transform duration-500">
          <Image
            src={productImg}
            alt={productName}
            fill
            unoptimized
            className="object-contain drop-shadow-[0_18px_22px_rgba(0,0,0,0.85)]"
          />
        </div>
      </Link>

      <div className="relative z-10 space-y-2.5">
        <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl px-3 py-2 backdrop-blur-md text-[10px] font-mono">
          <span className="flex items-center gap-1 text-amber-400 font-bold">
            <Star className="w-3 h-3 fill-amber-400" /> {rating.toFixed(1)}
          </span>
          <span className="flex items-center gap-1 text-rose-300 font-bold">
            <Clock className="w-3 h-3" />
            {String(timer.hours).padStart(2, "0")}h:{String(timer.minutes).padStart(2, "0")}m:
            {String(timer.seconds).padStart(2, "0")}s
          </span>
        </div>

        <button
          type="button"
          onClick={(e) => handleAddToCart(e)}
          className="w-full py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 shadow-lg transition-all active:scale-95 cursor-pointer"
        >
          {added ? <Check className="w-4 h-4" /> : <ShoppingBag className="w-4 h-4" />}
          <span>{added ? "Added to Cart" : "Claim Spotlight Deal"}</span>
        </button>
      </div>
    </div>
  );
}
